// Default options for html2pug.
export const defaultOptions = {
    collapse: true,
    commas: false,
    doubleQuotes: true,
    tabs: false,
    preserveTags: ['script', 'pre'],
    tabChar: '\t',
    whitespaceChar: '  '
};
var HtmlNodeType;
(function (HtmlNodeType) {
    HtmlNodeType[HtmlNodeType["Element"] = 1] = "Element";
    HtmlNodeType[HtmlNodeType["Text"] = 3] = "Text";
    HtmlNodeType[HtmlNodeType["CData"] = 4] = "CData";
    HtmlNodeType[HtmlNodeType["Comment"] = 8] = "Comment";
    HtmlNodeType[HtmlNodeType["Document"] = 9] = "Document";
    HtmlNodeType[HtmlNodeType["DocumentType"] = 10] = "DocumentType";
})(HtmlNodeType || (HtmlNodeType = {}));
export class Parser {
    constructor(options = defaultOptions) {
        this.options = options;
        this.parseHTML = (markup) => {
            if (markup.toLowerCase().trim().indexOf('<!doctype') === 0) {
                const doc = document.implementation.createHTMLDocument("");
                doc.documentElement.innerHTML = markup;
                // console.dir('A', doc)
                return doc;
            }
            if ('content' in document.createElement('template')) {
                // Template tag exists!
                const el = document.createElement('template');
                el.innerHTML = markup;
                // console.dir('B', el.content)
                return el.content;
            }
            // Template tag doesn't exist!
            const docfrag = document.createDocumentFragment();
            const el1 = document.createElement('body');
            el1.innerHTML = markup;
            for (const c of Array.from(el1.childNodes)) {
                docfrag.appendChild(c);
            }
            // console.dir('C', docfrag)
            return docfrag;
        };
        this.isMultiline = (text) => (text.split('\n') || []).length > 1;
        this.indent = (cnt) => this.tab.repeat(cnt);
        this.tab = this.options.tabs ? options.tabChar : options.whitespaceChar;
        this.quote = this.options.doubleQuotes ? `"` : `'`;
        this.space = this.options.commas ? `, ` : ` `;
        this.preserveTags = this.options.preserveTags.map(x => x.toUpperCase());
    }
    parse(content) {
        if (content === null) {
            return "";
        }
        if (typeof content === 'string') {
            const fragment = this.parseHTML(content);
            const sb = [];
            this.parseNodes(Array.from(fragment.childNodes), sb);
            return sb.join("\n");
        }
        return this.createMarkup(content);
    }
    parseNodes(children, sb, depth = 0) {
        let text;
        if (children.length) {
            for (const child of children) {
                text = this.createMarkup(child, depth);
                if (text != null) {
                    sb.push(text);
                }
            }
        }
    }
    createMarkup(el, depth = 0) {
        // console.group(">", el.tagName || '#TEXT', el.nodeType, depth)
        const sb = [];
        if (el.nodeType === HtmlNodeType.Text) {
            const text = el.textContent;
            if (text !== null && text.trim().length !== 0) {
                this.createText(sb, text, depth, '| ', true);
            }
        }
        else if (el.nodeType === HtmlNodeType.DocumentType) {
            sb.push('doctype html');
        }
        else {
            this.createElement(sb, el, depth);
        }
        const cs = this.canShorten(el) && this.options.collapse;
        if (el.nodeType !== HtmlNodeType.Text) {
            const children = this.activeChildNodes(el);
            this.parseNodes(children, sb, cs ? depth : depth + 1);
        }
        // console.groupEnd();
        if (sb.length === 0) {
            return null;
        }
        return (cs) ? sb.map((x, i) => i === 0 ? x : x === null || x === void 0 ? void 0 : x.trimStart()).join(": ") : sb.join("\n");
    }
    createText(sb, text, depth, prefix = '| ', preserve = false) {
        const segments = text.split('\n');
        const lines = [];
        segments.forEach((segment, index) => {
            const a = [];
            const trimmed_segment = segment.trim();
            if (trimmed_segment.length === 0 && (index === 0 || index == segments.length - 1)) {
                return;
            }
            a.push(this.indent(depth));
            if (prefix) {
                a.push(prefix);
            }
            if (segment.startsWith(' ') && !segment.startsWith('  ') && preserve) {
                a.push(' ');
            }
            if (trimmed_segment.length > 0) {
                a.push(trimmed_segment);
            }
            if (segment.endsWith(' ') && preserve) {
                a.push(' ');
            }
            lines.push(a.join(''));
        });
        sb.push(lines.join('\n'));
    }
    createElement(sb, node, depth) {
        const tagName = node.tagName.toLowerCase();
        const attributeList = [];
        const pugNode = [];
        let classes = [];
        let specialClasses = [];
        let shorten = false;
        pugNode.push(tagName);
        for (const a of Array.from(node.attributes)) {
            const name = a.name;
            const value = a.value;
            if (name === "id") {
                shorten = true;
            }
            else if (name === "class") {
                const c = value.split(" ");
                specialClasses = c.filter(x => /[:.\/]/.test(x));
                classes = c.filter(x => !(/[:.\/]/.test(x)));
                shorten = shorten || classes.length > 0;
            }
            else {
                attributeList.push(`${name}=${this.quote}${value}${this.quote}`);
            }
        }
        // Remove div tagName
        if (tagName === "div" && shorten) {
            pugNode.splice(0, 1);
        }
        if (node.id) {
            pugNode.push(`#${node.id}`);
        }
        if (classes.length) {
            pugNode.push(`.${classes.join('.')}`);
        }
        if (specialClasses.length > 0) {
            attributeList.push(`class=${this.quote}${specialClasses.join(" ")}${this.quote}`);
        }
        if (attributeList.length > 0) {
            pugNode.push(`(${attributeList.join(this.space)})`);
        }
        if (node.hasChildNodes() && node.childNodes.length === 1 && node.childNodes[0].nodeType === HtmlNodeType.Text) {
            const text = node.childNodes[0].textContent;
            if (text !== null && text.trim().length !== 0) {
                if (this.isMultiline(text)
                    && ((this.options.collapse && this.isMultiline(text.trim())) || this.options.collapse === false)) {
                    if (this.preserveTags.includes(node.nodeName)) {
                        pugNode.push('.\n');
                        this.createText(pugNode, text, depth + 1, "");
                    }
                    else {
                        pugNode.push('\n');
                        this.createText(pugNode, text, depth + 1);
                    }
                }
                else {
                    if (text.includes('\n'))
                        pugNode.push(` ${text.trim()}`);
                    else
                        pugNode.push(` ${text}`);
                }
            }
        }
        // console.log("pugNode:", pugNode)
        sb.push(`${this.indent(depth)}${pugNode.join("")}`);
    }
    canShorten(node) {
        const ac = this.activeChildNodes(node);
        if (ac.length === 1)
            return this.canShorten(ac[0]);
        else if (ac.length === 0)
            return true;
        return false;
    }
    activeChildNodes(node) {
        let children = Array.from(node.childNodes);
        if (node.nodeName === "TEMPLATE") {
            children = Array.from(node.content.childNodes);
        }
        const hasElements = (node.nodeType === HtmlNodeType.Element && children.filter(x => x.nodeType === HtmlNodeType.Element).length);
        const results = hasElements ?
            children.filter(x => { var _a; return x.nodeType === HtmlNodeType.Element || (x.nodeType === HtmlNodeType.Text && ((_a = x.textContent) === null || _a === void 0 ? void 0 : _a.trim())); })
            : [];
        // console.log(":: activeChildNodes", hasElements, results, node.nodeName)
        return results;
    }
}
//# sourceMappingURL=parser.js.map